import React, { FC, useMemo } from "react";
import { useInventoryVisibility } from "@hooks/api/useInventoryVisibility.hook";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import Skeleton from "react-loading-skeleton";
import S from "./StockAmountStatus.module.scss";

export interface StockAmountStatusProps {
    productNumber: string;
    variantId?: string;
    className?: string;
    productId?: string;
}

export const StockAmountStatus: FC<StockAmountStatusProps> = ({
    productNumber,
    variantId,
    className,
}) => {
    const { getDictionaryString } = useTranslations();

    // 1) Memoize so the array doesn't change on every render
    const requestItems = useMemo(() => {
        // If variantId is undefined, we only return productNumber
        return variantId ? [{ productNumber, variantId }] : [{ productNumber }];
    }, [productNumber, variantId]);

    // 2) Use the stable array in your hook
    const { data, loading, error } = useInventoryVisibility(requestItems);

    // 3) Derive the matched item
    const inventoryItem = useMemo(() => {
        if (!data || data.length === 0) return null;
        // Match by productNumber. If variantId is passed, also match that
        return data.find(
            (item) =>
                item.productId === productNumber &&
                (variantId ? item.retailVariantId === variantId : true)
        );
    }, [data, productNumber, variantId]);

    if (error) return null;
    if (loading) {
        return (
            <div className={cn(S.stockStatus, className)}>
                <Skeleton width={100} />
            </div>
        );
    }
    if (!inventoryItem) return null;

    // 4) Display logic
    const { quantityInStock, status } = inventoryItem;
    const hasInventory = status === "HasInventory";
    const isInStock = hasInventory && quantityInStock > 0;

    const colorClass = isInStock ? S.stockIconGreen : S.stockIconYellow;
    const displayText = isInStock
        ? `${quantityInStock} ${getDictionaryString("pcs")} ${getDictionaryString("inStock")}`
        : getDictionaryString("onItsWayRemote");

    return (
        <div className={cn(S.stockStatus, className)}>
            <span className={cn(S.stockIcon, colorClass)} />
            {displayText}
        </div>
    );
};
